import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import swal from "sweetalert";

const KEY = "eyJ0eXAiOiJKV1Qidfg%!#%$%LCdf#*&*JhbGciOiJSUzI1NiJ9";

const Icon = ({ name, size = 19 }) => {
  const paths = {
    chat: <><path d="M21 11.5a8.4 8.4 0 0 1-.9 3.8A8.5 8.5 0 0 1 12.5 20a8.4 8.4 0 0 1-3.8-.9L3 21l1.9-5.7a8.4 8.4 0 0 1-.9-3.8A8.5 8.5 0 0 1 12.5 3H13a8.5 8.5 0 0 1 8 8v.5Z"/></>,
    arrow: <><path d="m15 18-6-6 6-6"/></>,
    refresh: <><path d="M20 11a8 8 0 0 0-14.9-4M4 4v4h4M4 13a8 8 0 0 0 14.9 4M20 20v-4h-4"/></>,
    send: <><path d="m22 2-7 20-4-9-9-4Z"/><path d="M22 2 11 13"/></>,
    clock: <><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></>,
    shield: <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z"/><path d="m9 12 2 2 4-4"/></>,
    info: <><circle cx="12" cy="12" r="9"/><path d="M12 11v5M12 8h.01"/></>,
    inbox: <><rect x="3" y="4" width="18" height="16" rx="3"/><path d="M3 13h5l2 3h4l2-3h5"/></>,
  };
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{paths[name] || paths.chat}</svg>;
};

const Checkout = () => {
  const { id } = useParams();
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState("");
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState("");
  const sendingRef = useRef(false);
  const latestRequest = useRef(0);
  const chatRef = useRef(null);

  const loadViewTicketData = useCallback(async () => {
    const requestNumber = ++latestRequest.current;
    try {
      const response = await axios.post("/view-ticket", { id, key_s: KEY });
      if (requestNumber === latestRequest.current) {
        setMessages(Array.isArray(response?.data?.data) ? response.data.data : []);
        setNotice("");
      }
    } catch (error) {
      if (requestNumber === latestRequest.current) setNotice("Unable to refresh the conversation. Please try again.");
    }
  }, [id]);

  useEffect(() => {
    loadViewTicketData();
    return () => { latestRequest.current += 1; };
  }, [loadViewTicketData]);

  const submitReply = async (event) => {
    event.preventDefault();
    if (sendingRef.current) return;
    if (!message.trim()) {
      setErrors({ message: "Please enter a message before submitting." });
      return;
    }
    if (localStorage.getItem("auth_token") === null) {
      swal("Warning", "Please log in to send a reply.", "warning");
      return;
    }
    sendingRef.current = true;
    setSending(true);
    setErrors({});
    try {
      const response = await axios.post("/ticket-submit", {
        message: message.trim(), ticket_id: id, key_s: KEY,
      });
      const result = response?.data || {};
      if (result.status === "success") {
        setMessage("");
        await loadViewTicketData();
        swal("Success", result.message || "Your reply was submitted.", "success");
      } else if (result.status === "fail") {
        swal("Warning", result.message || "Unable to send your reply.", "warning");
      } else {
        setErrors(result.validation_error || { message: result.message || "Please check your message." });
      }
    } catch (error) {
      if (error?.response?.data?.validation_error) {
        setErrors(error.response.data.validation_error);
      } else {
        swal("Error", "Something went wrong. Please try again.", "error");
      }
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  const errorMessage = Array.isArray(errors?.message) ? errors.message.join(" ") : errors?.message;

  return (
    <>
      <style>{`
        html,body,#root{background:#00191D!important;color:#fff!important}
        .rmtv,.rmtv *{box-sizing:border-box}
        .rmtv{--navy:#00191D;--cyan:#16B8C7;--muted:#a2b9bd;--line:rgba(117,204,214,.19);font-family:inherit;font-size:14px;min-height:100vh;background:#00191D;color:#fff;padding:22px 4px 42px;line-height:1.55}
        .rmtv-wrap{max-width:1150px;margin:0 auto;width:100%}
        .rmtv-top{display:flex;justify-content:space-between;align-items:center;gap:12px;margin-bottom:19px}
        .rmtv-eyebrow{font-size:11px;font-weight:800;letter-spacing:.13em;text-transform:uppercase;color:#5fe0e9;margin-bottom:5px}
        .rmtv-heading{font-size:clamp(22px,3vw,29px);font-weight:800;margin:0;letter-spacing:-.6px;color:#fff}
        .rmtv-sub{margin:5px 0 0;color:var(--muted);font-size:13px}
        .rmtv-back,.rmtv-btn{display:inline-flex;align-items:center;justify-content:center;gap:8px;border-radius:12px;font-weight:750;text-decoration:none!important;transition:transform .18s ease,background .18s ease,border-color .18s ease}
        .rmtv-back{background:#092c31;color:#fff!important;border:1px solid var(--line);padding:10px 14px;white-space:nowrap;font-size:13px}
        .rmtv-back:hover{background:#10434a;transform:translateY(-2px)}
        .rmtv-grid{display:grid;grid-template-columns:minmax(0,1.65fr) minmax(280px,1fr);gap:16px;align-items:start}
        .rmtv-panel{background:#05252a;border:1px solid var(--line);border-radius:20px;overflow:hidden;box-shadow:0 12px 30px rgba(0,0,0,.12);animation:rmtvIn .35s ease both}
        .rmtv-panel-head{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:19px 20px;border-bottom:1px solid var(--line)}
        .rmtv-title{display:flex;align-items:center;gap:10px;margin:0;font-size:16px;font-weight:800;color:#fff}
        .rmtv-icon{display:inline-flex;align-items:center;justify-content:center;width:38px;height:38px;border-radius:11px;background:rgba(22,184,199,.14);color:#44d3df;flex:none}
        .rmtv-count{color:#90bfc4;font-size:12px;white-space:nowrap}
        .rmtv-refresh{display:inline-flex;align-items:center;justify-content:center;width:36px;height:36px;border-radius:11px;border:1px solid var(--line);background:#0a3238;color:#70dbe4;cursor:pointer;transition:transform .2s ease}
        .rmtv-refresh:hover{transform:rotate(45deg);background:#12444b}
        .rmtv-chat{padding:20px;min-height:300px;max-height:580px;overflow-y:auto;scrollbar-width:thin;scrollbar-color:#257b84 #05252a}
        .rmtv-row{display:flex;margin-bottom:17px;align-items:flex-end;gap:9px}
        .rmtv-row.user{justify-content:flex-end}.rmtv-row.admin{justify-content:flex-start}
        .rmtv-avatar{width:31px;height:31px;border-radius:10px;background:#fff;color:#07505b;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:850;flex:none}
        .rmtv-row.user .rmtv-avatar{order:2;background:#16B8C7;color:#00292e}
        .rmtv-bubble{max-width:min(78%,530px);padding:12px 14px;border-radius:15px;background:#fff;color:#18383d;border-bottom-left-radius:4px}
        .rmtv-row.user .rmtv-bubble{background:linear-gradient(125deg,#16B8C7,#65e1e7);color:#002b31;border-bottom-left-radius:15px;border-bottom-right-radius:4px}
        .rmtv-message{white-space:pre-wrap;overflow-wrap:anywhere;font-size:14px;font-weight:550;margin:0}
        .rmtv-meta{display:flex;align-items:center;gap:5px;margin-top:8px;font-size:11px;opacity:.7}
        .rmtv-empty{text-align:center;color:#9eb9bd;padding:70px 12px}.rmtv-empty svg{color:#3cc6d1;margin-bottom:10px}
        .rmtv-alert{margin:12px 20px 0;padding:10px 12px;border-radius:10px;border:1px solid rgba(255,144,144,.3);color:#ffb3b3;font-size:13px}
        .rmtv-side{display:flex;flex-direction:column;gap:16px}
        .rmtv-form{padding:20px}.rmtv-label{display:block;font-size:13px;font-weight:750;margin-bottom:9px;color:#e6f4f5}
        .rmtv-textarea{width:100%;min-height:185px;resize:vertical;padding:13px 14px;border-radius:12px;border:1px solid rgba(133,205,212,.3);background:#00191D;color:#fff;font-size:14px;line-height:1.6;font-family:inherit;outline:none;transition:border-color .18s,box-shadow .18s}
        .rmtv-textarea::placeholder{color:#77989d}.rmtv-textarea:focus{border-color:#16B8C7;box-shadow:0 0 0 3px rgba(22,184,199,.13)}
        .rmtv-error{display:block;color:#ffadad;font-size:12px;margin:7px 0}
        .rmtv-form-foot{display:flex;justify-content:space-between;align-items:center;gap:10px;margin-top:14px}
        .rmtv-hint{color:#8fb0b5;font-size:12px}
        .rmtv-btn{border:0;cursor:pointer;background:linear-gradient(115deg,#16B8C7,#5bdce6);color:#002c32;padding:12px 17px;font-size:14px;min-height:44px}
        .rmtv-btn:hover:not(:disabled){transform:translateY(-2px);background:linear-gradient(115deg,#36cbd6,#83e7ec)}
        .rmtv-btn:disabled{opacity:.65;cursor:not-allowed}
        .rmtv-help{background:#fff;color:#153a40;border:1px solid #d8e8e9;border-radius:20px;padding:21px}
        .rmtv-help-title{font-size:15px;font-weight:850;display:flex;align-items:center;gap:9px;margin:0 0 11px;color:#09383f}
        .rmtv-help-title svg{color:#009cab}.rmtv-help p{margin:0;color:#526a70;font-size:13px;line-height:1.8}
        .rmtv-help-tag{display:inline-flex;margin-top:15px;padding:7px 10px;border-radius:9px;background:#e9f9fa;color:#087581;font-size:12px;font-weight:750}
        @keyframes rmtvIn{from{opacity:0;transform:translateY(9px)}to{opacity:1;transform:translateY(0)}}
        @media(max-width:820px){.rmtv-grid{grid-template-columns:1fr}.rmtv-side{display:grid;grid-template-columns:1fr 1fr}.rmtv-chat{max-height:440px}}
        @media(max-width:580px){.rmtv{padding-top:14px}.rmtv-top{align-items:flex-start}.rmtv-heading{font-size:21px}.rmtv-sub{font-size:12px}.rmtv-back{padding:9px 10px;font-size:12px}.rmtv-panel-head{padding:14px}.rmtv-chat{padding:13px;max-height:380px}.rmtv-bubble{max-width:85%;padding:10px 12px}.rmtv-side{display:flex}.rmtv-form{padding:15px}.rmtv-help{padding:17px}.rmtv-form-foot{align-items:stretch;flex-direction:column}.rmtv-btn{width:100%}}
        @media(prefers-reduced-motion:reduce){.rmtv *{animation:none!important;transition:none!important}}
      `}</style>

      <main className="rmtv">
        <div className="rmtv-wrap">
          <header className="rmtv-top">
            <div>
              <div className="rmtv-eyebrow">RM Crown Market · Support Center</div>
              <h1 className="rmtv-heading">Ticket Conversation</h1>
              <p className="rmtv-sub">Review your conversation and reply to the support team.</p>
            </div>
            <Link className="rmtv-back" to="/support-ticket"><Icon name="arrow" size={16}/> All Tickets</Link>
          </header>

          <div className="rmtv-grid">
            <section className="rmtv-panel" aria-label="Ticket conversation">
              <div className="rmtv-panel-head">
                <h2 className="rmtv-title"><span className="rmtv-icon"><Icon name="chat"/></span> Conversation</h2>
                <div style={{display:"flex",alignItems:"center",gap:12}}>
                  <span className="rmtv-count">{messages.length} messages</span>
                  <button className="rmtv-refresh" type="button" title="Refresh messages" aria-label="Refresh messages" onClick={loadViewTicketData}><Icon name="refresh" size={17}/></button>
                </div>
              </div>
              {notice && <div className="rmtv-alert" role="alert">{notice}</div>}
              <div className="rmtv-chat" ref={chatRef}>
                {messages.length ? messages.map((item, index) => {
                  const isUser = item.admin_id === null;
                  return <div className={`rmtv-row ${isUser ? "user" : "admin"}`} key={item.id || index}>
                    <span className="rmtv-avatar">{isUser ? "ME" : "SP"}</span>
                    <div className="rmtv-bubble">
                      <p className="rmtv-message">{item.message}</p>
                      <span className="rmtv-meta"><Icon name="clock" size={12}/>{item.created_at}</span>
                    </div>
                  </div>;
                }) : <div className="rmtv-empty"><Icon name="inbox" size={35}/><div>No conversation found</div></div>}
              </div>
            </section>

            <aside className="rmtv-side">
              <section className="rmtv-panel">
                <div className="rmtv-panel-head"><h2 className="rmtv-title"><span className="rmtv-icon"><Icon name="send"/></span> Send a Reply</h2></div>
                <form className="rmtv-form" onSubmit={submitReply} noValidate>
                  <label htmlFor="rmtv-reply" className="rmtv-label">Your Message</label>
                  <textarea id="rmtv-reply" name="message" className="rmtv-textarea" placeholder="Write your message to our support team..." value={message} onChange={(event) => { setMessage(event.target.value); if (errors.message) setErrors({}); }} aria-invalid={Boolean(errorMessage)} aria-describedby={errorMessage ? "rmtv-error" : undefined}/>
                  {errorMessage && <span id="rmtv-error" className="rmtv-error" role="alert">{errorMessage}</span>}
                  <div className="rmtv-form-foot"><span className="rmtv-hint">Describe your issue clearly.</span><button className="rmtv-btn" type="submit" disabled={sending}><Icon name="send" size={16}/>{sending ? "Sending..." : "Send Reply"}</button></div>
                </form>
              </section>
              <section className="rmtv-help"><h3 className="rmtv-help-title"><Icon name="shield"/> Support & Privacy</h3><p>Your conversation is linked to this support ticket. Avoid sharing passwords, one-time codes, or other sensitive account details in messages.</p><span className="rmtv-help-tag"><Icon name="info" size={14}/> Secure support channel</span></section>
            </aside>
          </div>
        </div>
      </main>
    </>
  );
};

export default Checkout;
